import { anyMoves, captures, setupBlackOptions, setupWhiteOptions } from './rules';
import { BLACK, type Board, type Color, type GameState, type Move, N, PHASE, WHITE } from './types';

export function createBoard(): Board {
  return Array.from({ length: N }, (_, r) =>
    Array.from({ length: N }, (_, c) => ((r + c) % 2 === 0 ? BLACK : WHITE)),
  );
}

export function initialState(): GameState {
  return {
    board: createBoard(),
    phase: PHASE.SETUP_B,
    turn: BLACK,
    sel: null,
    removed: null,
    dir: null,
    moves: [],
    caps: { black: 0, white: 0 },
    winner: null,
    turnCount: 0,
  };
}

export function cloneState(s: GameState): GameState {
  return {
    board: s.board.map((row) => row.slice()),
    phase: s.phase,
    turn: s.turn,
    sel: s.sel ? { ...s.sel } : null,
    removed: s.removed ? { ...s.removed } : null,
    dir: s.dir,
    moves: s.moves.map((m) => ({ ...m })),
    caps: { ...s.caps },
    winner: s.winner,
    turnCount: s.turnCount,
  };
}

/**
 * Result of an attempted state transition. `changed` is true only when the
 * board changed (a setup removal or a capture); selection-only updates do
 * not count as history-worthy changes.
 */
export interface Transition {
  state: GameState;
  changed: boolean;
  /** If the transition was a capture, the position of the captured piece (for animation). */
  capturedAt?: { r: number; c: number };
  /** If the transition moved a piece, source and destination (for animation). */
  moved?: { from: { r: number; c: number }; to: { r: number; c: number } };
}

function noChange(s: GameState): Transition {
  return { state: s, changed: false };
}

/**
 * Apply a cell click to the current state. Returns a transition describing
 * the new state plus animation hints.
 */
export function applyCellClick(s: GameState, r: number, c: number): Transition {
  if (s.phase === PHASE.OVER) return noChange(s);

  if (s.phase === PHASE.SETUP_B) {
    return applySetupBlack(s, r, c);
  }
  if (s.phase === PHASE.SETUP_W) {
    return applySetupWhite(s, r, c);
  }
  if (s.phase === PHASE.SELECT) {
    return applySelect(s, r, c);
  }
  if (s.phase === PHASE.CAPTURE) {
    return applyCapture(s, r, c);
  }
  if (s.phase === PHASE.MULTI) {
    return applyMulti(s, r, c);
  }
  return noChange(s);
}

function applySetupBlack(s: GameState, r: number, c: number): Transition {
  if (!setupBlackOptions(s.board).some((o) => o.r === r && o.c === c)) {
    return noChange(s);
  }
  const next = cloneState(s);
  next.board[r][c] = null;
  next.removed = { r, c };
  next.phase = PHASE.SETUP_W;
  next.turn = WHITE;
  return { state: next, changed: true };
}

function applySetupWhite(s: GameState, r: number, c: number): Transition {
  if (!s.removed) return noChange(s);
  if (!setupWhiteOptions(s.board, s.removed.r, s.removed.c).some((o) => o.r === r && o.c === c)) {
    return noChange(s);
  }
  const next = cloneState(s);
  next.board[r][c] = null;
  next.phase = PHASE.SELECT;
  next.turn = BLACK;
  if (!anyMoves(next.board, BLACK)) {
    next.phase = PHASE.OVER;
    next.winner = WHITE;
  }
  return { state: next, changed: true };
}

function applySelect(s: GameState, r: number, c: number): Transition {
  if (s.board[r][c] !== s.turn) return noChange(s);
  const ms = captures(s.board, r, c);
  if (ms.length === 0) return noChange(s);
  const next = cloneState(s);
  next.sel = { r, c };
  next.moves = ms;
  next.phase = PHASE.CAPTURE;
  return { state: next, changed: false };
}

function applyCapture(s: GameState, r: number, c: number): Transition {
  const mv = s.moves.find((m) => m.tr === r && m.tc === c);
  if (mv) return doCapture(s, mv);

  // Switching to a different own piece with captures available
  if (s.board[r][c] === s.turn && !(s.sel && s.sel.r === r && s.sel.c === c)) {
    const ms = captures(s.board, r, c);
    if (ms.length > 0) {
      const next = cloneState(s);
      next.sel = { r, c };
      next.moves = ms;
      return { state: next, changed: false };
    }
  }

  // Click on the currently selected piece → deselect
  if (s.sel && s.sel.r === r && s.sel.c === c) {
    const next = cloneState(s);
    next.sel = null;
    next.moves = [];
    next.phase = PHASE.SELECT;
    return { state: next, changed: false };
  }

  // Otherwise ignore (don't silently deselect on misclick).
  return noChange(s);
}

function applyMulti(s: GameState, r: number, c: number): Transition {
  const mv = s.moves.find((m) => m.tr === r && m.tc === c);
  if (mv) return doCapture(s, mv);
  return noChange(s);
}

function doCapture(s: GameState, mv: Move): Transition {
  const next = cloneState(s);
  next.board[mv.tr][mv.tc] = next.board[mv.fr][mv.fc];
  next.board[mv.fr][mv.fc] = null;
  next.board[mv.cr][mv.cc] = null;
  next.caps[next.turn]++;
  next.sel = { r: mv.tr, c: mv.tc };

  const cont = captures(next.board, mv.tr, mv.tc, mv.dir);
  if (cont.length > 0) {
    next.moves = cont;
    next.dir = mv.dir;
    next.phase = PHASE.MULTI;
    return {
      state: next,
      changed: true,
      capturedAt: { r: mv.cr, c: mv.cc },
      moved: { from: { r: mv.fr, c: mv.fc }, to: { r: mv.tr, c: mv.tc } },
    };
  }

  return {
    state: finishTurn(next),
    changed: true,
    capturedAt: { r: mv.cr, c: mv.cc },
    moved: { from: { r: mv.fr, c: mv.fc }, to: { r: mv.tr, c: mv.tc } },
  };
}

export function endTurn(s: GameState): Transition {
  if (s.phase !== PHASE.MULTI) return noChange(s);
  return { state: finishTurn(cloneState(s)), changed: true };
}

/**
 * If a loaded state is mid-turn for a side controlled by the AI, advance it
 * to a phase the AI can act from on its next turn.
 *
 * Background: the AI's worker searches from PHASE.SELECT (or SETUP_*). If a
 * mid-multi (PHASE.MULTI) or mid-selection (PHASE.CAPTURE) state for the
 * AI's color ever lands in localStorage — e.g., a refresh between sub-jumps
 * of an AI multi-capture — the AI would be unable to generate moves and the
 * UI would freeze. This helper normalizes the loaded state to a phase the
 * worker can resume from.
 *
 * For MULTI: act as if the AI ended its turn here (the partial multi counts).
 * For CAPTURE: drop the selection; the AI will re-select.
 * For SELECT / SETUP_* / OVER: no-op.
 */
export function recoverIfMidAITurn(s: GameState, aiColor: Color | null): GameState {
  if (aiColor === null) return s;
  if (s.turn !== aiColor) return s;
  if (s.phase === PHASE.MULTI) {
    return finishTurn(cloneState(s));
  }
  if (s.phase === PHASE.CAPTURE) {
    const next = cloneState(s);
    next.phase = PHASE.SELECT;
    next.sel = null;
    next.moves = [];
    return next;
  }
  return s;
}

function finishTurn(next: GameState): GameState {
  const opp: Color = next.turn === BLACK ? WHITE : BLACK;
  next.sel = null;
  next.moves = [];
  next.dir = null;
  next.turnCount += 1;
  if (!anyMoves(next.board, opp)) {
    // Game over. Convention: state.turn is the side WHO WOULD MOVE NEXT —
    // i.e., the loser. This keeps the negamax-style score symmetry clean
    // for AI search (see src/ai/search.ts) and doesn't change anything the
    // UI displays (UI reads state.winner at OVER, never state.turn).
    next.phase = PHASE.OVER;
    next.winner = next.turn;
    next.turn = opp;
  } else {
    next.turn = opp;
    next.phase = PHASE.SELECT;
  }
  return next;
}
